import { Image, Spinner } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { VStack } from '@/components/ui/stack'
import { Text } from '@/components/ui/typography'
import useCustomToast from '@/hooks/toasts'
import { queryClient } from '@/QueryClient'
import { getApprovals } from '@/store/tasksApprovals'
import { bulkApprovals } from '@/store/tasksApprovals/detailsDrawer'
import {
    childItemsAtom,
    generateDataforBuklApproval,
    rejectReasonForAllAtom,
    selectedOptionForAllAtom,
} from '@/store/tasksApprovals/detailsDrawer/sapGrcRoleAccess'
import { IApprovalDetail } from '@/types/tasksApprovals'

const Container = ({ itemDetails, itemId, onClose }: { itemDetails: IApprovalDetail; itemId: string; onClose: () => void }) => {
    const { t } = useTranslation()

    const [isApproveSet, setIsApproveSet] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [selectedOption] = useAtom(selectedOptionForAllAtom)
    const [rejectReason] = useAtom(rejectReasonForAllAtom)
    const [childItems] = useAtom(childItemsAtom)
    const [addToast] = useCustomToast()
    const rejectedItems = childItems.filter((item) => item.isRejected)
    const rejectReasonMinLength = 3
    const canBeApproved = itemDetails?.approval_info?.can_be_approved ?? true

    const canSubmit =
        (selectedOption === 'approve' ||
            (selectedOption === 'reject' && rejectReason && rejectReason.length >= rejectReasonMinLength) ||
            (selectedOption === 'mixed' &&
                rejectedItems.every((item) => item.rejectReason && item.rejectReason.length >= rejectReasonMinLength))) &&
        itemDetails &&
        typeof itemDetails?.approval_info?.can_be_approved === 'undefined'

    const onSubmit = () => {
        setIsApproveSet(true)
    }

    const onConfirm = () => {
        setIsLoading(true)
        void bulkApprovals('SAP_GRC_ACCESS_REQUEST_DETAILS', generateDataforBuklApproval(childItems), 'approvals', false, itemId).then(
            (res) => {
                if (res && res.ok) {
                    void queryClient.invalidateQueries({ queryKey: ['approvals'] })
                    void getApprovals(true)
                    addToast('success', t('tasksApprovals.drawer.submissionSuccessful'))
                }
                onClose()
                setIsLoading(false)
            },
        )
    }

    const onCancel = () => {
        if (isApproveSet) {
            setIsApproveSet(false)
        } else {
            onClose()
        }
    }

    const infoMessage = () => {
        const missingRejectReasonForAll = () => {
            return (
                <Box display="flex" alignItems="center" pb="24px">
                    <Image src="/assets/alert_icon.svg" maxW="14px" />
                    <Text fontSize="14px" pl="8px">
                        {t('tasksApprovals.sapGrcAccessRequestDrawer.footer.reasonForAll')}
                    </Text>
                </Box>
            )
        }

        const missingRejectReason = () => {
            return (
                <Box display="flex" alignItems="center" pb="24px">
                    <Image src="/assets/alert_icon.svg" maxW="14px" />
                    <Text fontSize="14px" pl="8px">
                        {t('tasksApprovals.sapGrcAccessRequestDrawer.footer.reason')}
                    </Text>
                </Box>
            )
        }

        const cantBeApprovedMessage = () => {
            return (
                <Box pb="24px" fontSize="14px">
                    <Box display="flex" alignItems="center">
                        <Image src="/assets/alert_icon.svg" maxW="14px" />
                        <Text pl="8px">{t('tasksApprovals.sapGrcAccessRequestDrawer.footer.cantApprove.firstLine')}</Text>
                    </Box>
                    <VStack pt="18px" gap="2px" align="stretch">
                        <Box>
                            <Text>{t('tasksApprovals.sapGrcAccessRequestDrawer.footer.cantApprove.text_1')}</Text>
                        </Box>
                        <Box>
                            <Text>{t('tasksApprovals.sapGrcAccessRequestDrawer.footer.cantApprove.text_2')}</Text>
                        </Box>
                        <Box>
                            <Text>{t('tasksApprovals.sapGrcAccessRequestDrawer.footer.cantApprove.text_3')}</Text>
                        </Box>
                    </VStack>
                </Box>
            )
        }

        const selectAllItems = () => {
            return (
                <Box display="flex" alignItems="center" pb="24px">
                    <Image src="/assets/alert_icon.svg" maxW="14px" />
                    <Text fontSize="14px" pl="8px">
                        {t('tasksApprovals.sapGrcAccessRequestDrawer.footer.selectAllItems')}
                    </Text>
                </Box>
            )
        }

        if (!canBeApproved) return cantBeApprovedMessage()
        else if (itemDetails && !rejectReason && selectedOption == 'reject') return missingRejectReasonForAll()
        else if (!canSubmit && selectedOption == 'mixed') return missingRejectReason()
        else if (itemDetails && !selectedOption) return selectAllItems()
    }

    return (
        <>
            {infoMessage()}
            <Button variant="secondary" mr="16px" onClick={onCancel}>
                {isApproveSet ? t('tasksApprovals.drawer.buttons.cancel') : t('tasksApprovals.drawer.buttons.close')}
            </Button>
            {!isApproveSet && canBeApproved && (
                <>
                    <Button
                        disabled={!canSubmit}
                        variant="primary"
                        onClick={onSubmit}
                        letterSpacing="1.5px"
                        textTransform="uppercase"
                        fontWeight="bold"
                    >
                        {t('tasksApprovals.drawer.buttons.submit')}
                    </Button>
                </>
            )}

            {isApproveSet && (
                <Button
                    variant="primary"
                    onClick={() => onConfirm()}
                    textTransform="uppercase"
                    letterSpacing="1.5px"
                    minW="254px"
                    disabled={isLoading || !canSubmit}
                >
                    {isLoading ? <Spinner /> : t('tasksApprovals.drawer.buttons.confirmSubmit')}
                </Button>
            )}
        </>
    )
}

export default Container
